<template>
  <Transition
    appear
    name="modal"
  >
    <div
      class="modal-mask"
      :class="{ $isMobile: $isMobile }"
    >
      <div
        aria-hidden="true"
        class="modal-mask__backdrop"
        @click="clickOutside"
      />
      <div
        ref="modalElement"
        :aria-label="title || undefined"
        aria-modal="true"
        class="modal column no-wrap"
        role="dialog"
        :style="modalStyle"
        tabindex="-1"
        @click.stop
        @keydown="handleKeydown"
      >
        <div class="header row q-pa-md justify-between">
          <slot name="header">
            <div
              v-if="title"
              class="bold row flex-center text-paragraph"
            >
              {{ title }}
            </div>

            <BaseButton
              v-if="!hideCloseButton"
              :aria-label="$t('global.close')"
              class="close-btn"
              dense
              flat
              icon="ion-md-close"
              @click="close"
            />
          </slot>
        </div>

        <div class="body q-pa-md relative">
          <slot />
        </div>

        <div class="footer row q-pa-sm justify-end">
          <slot name="footer">
            <div class="row q-gutter-x-sm items-center">
              <slot name="buttons">
                <BaseButton
                  v-for="(buttonConfig, key) in buttons.filter(
                    (button) => !button.hidden
                  )"
                  :key="key"
                  :aria-label="$t('modal_popup.toggle_button')"
                  :class="buttonConfig.class"
                  :color="buttonConfig.color"
                  :disabled="buttonConfig.disabled"
                  :loading="buttonConfig.loading"
                  @click="buttonCallback(buttonConfig)"
                >
                  {{ buttonConfig.label }}
                </BaseButton>
              </slot>
            </div>
          </slot>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import { getCurrentInstance } from "vue";

import BaseButton from "shared/components/base/BaseButton";
import useModal from "shared/composables/useModal";

export default {
  name: "BaseModalPopup",
  components: {
    BaseButton,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    modalStyle: {
      type: Object,
      default: () => ({}),
    },
    buttons: {
      type: Array,
      default: () => [],
    },
    hideCloseButton: {
      type: Boolean,
    },
    persistent: {
      type: Boolean,
    },
  },
  emits: ["before-close", "close"],
  setup() {
    const { parent } = getCurrentInstance();
    const modalName = parent.type.name;
    const { close } = useModal(modalName);

    return { close };
  },
  mounted() {
    this.previouslyFocusedElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    this.$nextTick(() => {
      this.focusFirstElement();
    });
  },
  beforeUnmount() {
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
    }
  },
  methods: {
    clickOutside() {
      if (!this.persistent) {
        this.close();
      }
    },
    getFocusableElements() {
      const { modalElement } = this.$refs;
      if (!modalElement) return [];

      const selector = [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "textarea:not([disabled])",
        '[tabindex]:not([tabindex="-1"])',
      ].join(", ");

      return Array.from(modalElement.querySelectorAll(selector)).filter(
        (element) => element.offsetParent !== null
      );
    },
    focusFirstElement() {
      const focusable = this.getFocusableElements();

      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        this.$refs.modalElement?.focus();
      }
    },
    handleKeydown(event) {
      if (event.key === "Escape") {
        if (!this.hideCloseButton) {
          this.tryClose();
        }

        return;
      }

      if (event.key !== "Tab") return;

      const focusable = this.getFocusableElements();

      if (focusable.length === 0) {
        event.preventDefault();

        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    tryClose() {
      let hasCanceled = false;

      const cancel = () => {
        hasCanceled = true;
      };

      this.$emit("before-close", cancel);
      if (hasCanceled) return;

      this.$emit("close");
      this.close();
    },
    async buttonCallback(buttonConfig) {
      let hasCanceled = false;

      const cancel = () => {
        hasCanceled = true;
      };

      if ("onClick" in buttonConfig && buttonConfig.onClick) {
        await buttonConfig.onClick(cancel);
      }

      if (hasCanceled) return;

      if ("closeOnClick" in buttonConfig && !buttonConfig.closeOnClick) return;
      this.tryClose();
    },
  },
};
</script>

<style lang="scss" scoped>
.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .streem-modal2,
.modal-leave-to .streem-modal2 {
  transform: scale(1.1);
}

.modal-mask {
  position: fixed;
  inset: 0;
  transition: opacity 0.3s ease;
  z-index: 4000;
  display: flex;
  justify-content: center;
  align-items: center;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: transparent;
  }
}

.modal {
  position: fixed;
  margin: 20px auto;
  width: 568px;
  max-height: calc(100% - 40px);
  max-width: 100%;
  border-radius: 4px;
  overflow: hidden;
  box-shadow:
    0 3px 6px -4px rgb(0 0 0 / 12%),
    0 6px 16px rgb(0 0 0 / 8%),
    0 9px 28px 8px rgb(0 0 0 / 5%),
    inset 0 -1px 0 $pale-white;
  background: $white;

  .body {
    flex: 1;
    min-height: 132px;
    overflow: auto;
  }

  .footer {
    height: 53px;
    background: $white;
    box-shadow: inset 0 1px 0 $pale-white;
  }
}
</style>
